import React, { useMemo, useState } from 'react';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { Faculty } from '@/wpt-lib/interfaces/Faculty';
import { Tile } from '@/components/Tile';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import FacultyEditorTable from '@/components/pages/Admin/Global/FacultyEditorTable';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { isNullish } from '@/wpt-lib/utils/isNullish';

interface UpdateFacultyDto {
    updates: {
        id: number;
        label?: string;
        inactive?: boolean;
    }[];
}

const FacultyEditor = () => {
    const { currentAssignment } = useAssignment();
    const { error, loading, responseData, makeRequest } = useGetRequest<ListResponse<Faculty>>();
    const { error: saveError, loading: saveLoading, makeRequest: save } = usePutRequest<Faculty[], UpdateFacultyDto>();

    const [updated, setUpdated] = useState<Faculty[]>([]);

    const loadFaculties = async () => {
        try {
            makeRequest(`faculty`, {
                params: {
                    all: true,
                },
            });
        } catch (e) {
            // handled by hook
        }
    };

    useAsyncEffect(loadFaculties, []);

    const onUpdateFaculty = (faculty: Faculty) => {
        setUpdated([...(updated || []).filter((f) => f.id !== faculty.id), faculty]);
    };

    const handleSave = async () => {
        if (updated.length === 0) return;

        await save('faculty', { updates: updated }, { params: { assignment: currentAssignment?.id } });
        setUpdated([]);
        setUpdated([]);
        loadFaculties();
    };

    const faculties = useMemo(() => {
        const list =
            responseData?.items?.map((faculty) => ({
                ...faculty,
                ...(updated.find((updatedFaculty) => updatedFaculty.id === faculty.id) ?? {}),
                changed: updated.some((updatedFaculty) => updatedFaculty.id === faculty.id),
            })) ?? [];

        list.sort((a, b) => a.name.localeCompare(b.name));
        return list;
    }, [responseData, updated]);

    return (
        <Tile colspan={3} heading={'Faculties'} collapsable rounded={'lg'} borderWidth={1} borderColor={'gray.100'}>
            <VStack alignItems={'flex-start'} spacing={3}>
                <Text>This section allows you to update faculty names and mark them active or inactive.</Text>
                <StateDisplay
                    data={faculties}
                    loading={loading}
                    error={!!error}
                    errorRender={() => <SimpleAlert variant="subtle">Could not load faculty list</SimpleAlert>}
                    render={() => {
                        return (
                            <VStack w="100%" alignItems={'flex-end'}>
                                <FacultyEditorTable faculties={faculties} onUpdate={onUpdateFaculty} />
                                <HStack>
                                    {updated.length > 0 && (
                                        <Text>
                                            {updated.length} facult{updated.length === 1 ? 'y' : 'ies'} with pending
                                            changes
                                        </Text>
                                    )}
                                    {!isNullish(saveError) && (
                                        <Text>Error saving changes, please refresh page and try again.</Text>
                                    )}
                                    <Button
                                        isDisabled={updated.length === 0 || saveLoading}
                                        onClick={() => setUpdated([])}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        isDisabled={updated.length === 0}
                                        isLoading={saveLoading}
                                        onClick={handleSave}
                                        colorScheme="blue"
                                    >
                                        Save
                                    </Button>
                                </HStack>
                            </VStack>
                        );
                    }}
                />
            </VStack>
        </Tile>
    );
};

export default FacultyEditor;
